<template>
  <div class="inline-block">
    <button
      class="w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200 text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-500 dark:hover:text-gray-400"
      @click="toggleModal"
    >
      <i
        v-if="sentiment === 'POSITIVE'"
        class="fa-regular fa-thumbs-up text-[12px]"
      ></i>
      <i v-else class="fa-regular fa-thumbs-down text-[12px]"></i>
    </button>

    <regular-modal
      ref="feedbackModal"
      title="Research Feedback"
      @close="resetForm"
    >
      <template #body>
        <div class="p-6">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Help us improve our Research Compass by providing feedback on this
            {{ itemTypeDisplay }}.
          </p>

          <div class="space-y-6">
            <!-- Sentiment Toggle -->
            <div>
              <label
                class="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-3"
                >Your Sentiment</label
              >
              <div class="flex gap-4">
                <button
                  class="flex-1 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-3 font-bold"
                  :class="
                    feedbackForm.sentiment === 'POSITIVE'
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-white dark:bg-[#050a0c] border-gray-200 dark:border-gray-800 text-gray-400 hover:border-emerald-500/50'
                  "
                  @click="feedbackForm.sentiment = 'POSITIVE'"
                >
                  <i class="fas fa-thumbs-up"></i> Accurate
                </button>
                <button
                  class="flex-1 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-3 font-bold"
                  :class="
                    feedbackForm.sentiment === 'NEGATIVE'
                      ? 'bg-rose-500 border-rose-500 text-white'
                      : 'bg-white dark:bg-[#050a0c] border-gray-200 dark:border-gray-800 text-gray-400 hover:border-rose-500/50'
                  "
                  @click="feedbackForm.sentiment = 'NEGATIVE'"
                >
                  <i class="fas fa-thumbs-down"></i> Needs Work
                </button>
              </div>
            </div>

            <!-- Predefined Items -->
            <div>
              <label
                class="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-3"
                >Specific Feedback</label
              >
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="item in feedbackOptions"
                  :key="item"
                  class="px-3 py-1.5 rounded-lg border text-xs font-bold transition-all"
                  :class="
                    feedbackForm.feedbackItems.includes(item)
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  "
                  @click="toggleFeedbackItem(item)"
                >
                  {{ item }}
                </button>
              </div>
            </div>

            <!-- Other Comments -->
            <div>
              <label
                class="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-3"
                >Additional Comments</label
              >
              <textarea
                v-model="feedbackForm.otherComments"
                class="w-full bg-gray-50 dark:bg-[#050a0c] border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Tell us more about your experience..."
              ></textarea>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          class="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700"
          @click="toggleModal"
        >
          Cancel
        </button>
        <button
          class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center gap-3 ml-3"
          :disabled="formSubmitting"
          @click="submit"
        >
          <i v-if="formSubmitting" class="fas fa-circle-notch fa-spin"></i>
          Submit Feedback
        </button>
      </template>
    </regular-modal>
  </div>
</template>

<script>
import * as utils from "../../utils";
import RegularModal from "../Modals/Regular.vue";

const { keysToSnake } = utils;

export default {
  components: {
    RegularModal,
  },
  props: {
    sentiment: String, // 'POSITIVE' or 'NEGATIVE'
    itemType: String, // 'SEARCH_PHRASE', 'PAPER', 'EXPERT', 'SUMMARY', 'TABLE' etc
    itemId: String, // The hashedId of the item being reviewed
    ancillaryInformation: [String, Number],
    ancillaryInformationLabel: String,
    researchCompassHashedId: String,
  },
  data() {
    return {
      formSubmitting: false,
      feedbackForm: {
        sentiment: this.sentiment,
        itemType: this.itemType,
        itemId: this.itemId,
        ancillaryInformation: this.ancillaryInformation,
        ancillaryInformationLabel: this.ancillaryInformationLabel,
        feedbackItems: [],
        otherComments: null,
      },
    };
  },
  computed: {
    itemTypeDisplay() {
      return (this.itemType || "item").toLowerCase().replace("_", " ");
    },
    feedbackOptions() {
      if (this.feedbackForm.sentiment === "POSITIVE") {
        if (this.itemType === "COLLABORATION_OPPORTUNITY") {
          return [
            "Highly Relevant",
            "Useful Suggestion",
            "Creative",
            "Logical Extension",
          ];
        }
        return [
          "Highly Relevant",
          "Accurate Summary",
          "Useful Citation",
          "Good Comparison",
        ];
      }
      return [
        "Irrelevant",
        "Factually Incorrect",
        "Bad Formatting",
        "Broken Link",
      ];
    },
  },
  methods: {
    toggleModal() {
      this.$refs.feedbackModal.toggleModal();
    },
    toggleFeedbackItem(item) {
      const index = this.feedbackForm.feedbackItems.indexOf(item);
      if (index === -1) {
        this.feedbackForm.feedbackItems.push(item);
      } else {
        this.feedbackForm.feedbackItems.splice(index, 1);
      }
    },
    resetForm() {
      this.feedbackForm = {
        sentiment: this.sentiment,
        itemType: this.itemType,
        itemId: this.itemId,
        ancillaryInformation: this.ancillaryInformation,
        ancillaryInformationLabel: this.ancillaryInformationLabel,
        feedbackItems: [],
        otherComments: null,
      };
    },
    submit() {
      this.formSubmitting = true;
      const baseUrl = `/research-compass/${
        this.researchCompassHashedId || this.$route.params.researchCompassId
      }/feedback/`;
      const params = new URLSearchParams();
      if (this.$route.params.collaborationSpaceId) {
        params.append(
          "collaboration_space_id",
          this.$route.params.collaborationSpaceId,
        );
      }
      if (this.$route.params.viewKey) {
        params.append("view_key", this.$route.params.viewKey);
      }
      const url = params.toString()
        ? `${baseUrl}?${params.toString()}`
        : baseUrl;

      const axios = this.$route.params.viewKey
        ? this.$api.axiosPublic
        : this.$api.axios;

      axios.post(url, keysToSnake(this.feedbackForm)).then(() => {
        this.formSubmitting = false;
        if (this.$toast) this.$toast.success("Thank you for your feedback!");
        this.toggleModal();
      });
    },
  },
};
</script>
